import { useParams, Link, useNavigate } from "react-router-dom"
import { useStoreDetail } from "@/graphql/hooks/useStoreDetail"
import { useAuthContext } from "@/contexts/AuthContext"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import {
  MapPin,
  Phone,
  Mail,
  Globe,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  ExternalLink,
  Calendar,
  Trophy,
  Plus,
  Edit,
} from "lucide-react"
import { AvatarUpload } from "@/components/ui/avatar-upload"
import { CoverUpload } from "@/components/ui/cover-upload"
import { useCountries, useRegions } from "@/graphql"
import { CountryFlag } from "@/components/ui/country-flag"

const randomPlaceholderLogo = (id: number) => {
  const placeholders = [1, 2, 3, 4, 5] // tus imágenes disponibles
  const lastDigit = id % 10 // obtiene el último dígito
  const index = placeholders[lastDigit % placeholders.length]
  return `/img/placeholder-logo-tienda${index}.png`
}

const randomPlaceholderCover = (id: number) => {
  const placeholders = [1, 2, 3] // tus imágenes disponibles
  const lastDigit = id % 10 // obtiene el último dígito
  const index = placeholders[lastDigit % placeholders.length] // mapea a 1, 2 o 3
  return `/img/placeholder-tienda${index}.png`
}

const randomPlaceholderAvatar = (id: number) => {
  const placeholders = [1, 2, 3, 4, 5] // tus imágenes disponibles
  const lastDigit = id % 10 // obtiene el último dígito
  const index = placeholders[lastDigit % placeholders.length]
  return `/img/placeholder-user${index}.png`
}

const getGameBadgeClass = (gameName: string): string => {
  const n = gameName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
  if (n.includes("primer bloque"))
    return "bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30 hover:bg-amber-500/30"
  if (n.includes("primera era"))
    return "bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-500/30 hover:bg-violet-500/30"
  if (n.includes("imperio"))
    return "bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30 hover:bg-blue-500/30"
  if (n.includes("furia"))
    return "bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30 hover:bg-red-500/30"
  return ""
}

const StoreDetail = () => {
  const { storeId } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, userInfo } = useAuthContext()
  const { store, loading, error } = useStoreDetail(parseInt(storeId || "0"))
  const { countriesMap } = useCountries()
  const { regionsMap } = useRegions()

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingScreen />
        </div>
      </Layout>
    )
  }

  // Check if user can create tournaments for this store
  const canCreateTournament =
    isAuthenticated &&
    userInfo?.User?.Person?.Store &&
    userInfo.User.Person.Store.active === 1 &&
    userInfo.User.Person.Store.id === store?.id

  const canUpdateImages =
    isAuthenticated &&
    store &&
    userInfo?.User?.Person?.Store &&
    userInfo?.User?.Person?.Store.id === store.id

  if (error || !store) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4">Tienda no encontrada</h2>
          <p className="text-muted-foreground mb-6">
            No se pudo cargar la información de la tienda.
          </p>
          <Link to="/stores">
            <Button>Volver a Tiendas</Button>
          </Link>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Hero Cover */}
        <div className="relative mb-0 rounded-2xl overflow-hidden">
          {canUpdateImages ? (
            <CoverUpload
              src={store.urlCover || randomPlaceholderCover(Number(storeId))}
              name={store.name || "Tienda"}
              personId={store.personId || 0}
              type="store.cover"
            />
          ) : (
            <div className="relative w-full rounded-2xl overflow-hidden bg-muted h-56 sm:h-72 md:h-80 lg:h-96">
              <img
                src={store.urlCover || randomPlaceholderCover(Number(storeId))}
                alt={store.name}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                onError={(e) => {
                  e.currentTarget.style.display = "none"
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            </div>
          )}
        </div>

        {/* Store Info Card — overlaps cover */}
        <div className="relative -mt-16 mb-8 px-2 sm:px-4">
          <Card className="border-border/60 bg-card shadow-lg">
            <CardContent className="p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start gap-5">
                {/* Logo */}
                <div className="flex-shrink-0 mx-auto md:mx-0 -mt-14 md:-mt-16">
                  {canUpdateImages ? (
                    <AvatarUpload
                      src={store.urlLogo || randomPlaceholderLogo(Number(storeId))}
                      name={store.name || "Tienda"}
                      personId={store.personId || 0}
                      type="store.logo"
                    />
                  ) : (
                    <img
                      src={store.urlLogo || randomPlaceholderLogo(Number(storeId))}
                      alt={`${store.name} logo`}
                      className="w-24 h-24 object-contain rounded-xl border-2 border-border bg-card shadow-md"
                    />
                  )}
                </div>

                {/* Name / description / actions */}
                <div className="flex-1 min-w-0 text-center md:text-left">
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-1">
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
                      {store.name}
                    </h1>
                    {store.active === 1 ? (
                      <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-xs">
                        Activa
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs">
                        Inactiva
                      </Badge>
                    )}
                  </div>

                  {store.description && (
                    <p className="text-muted-foreground text-sm md:text-base mb-4 leading-relaxed">
                      {store.description}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    {canUpdateImages && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/stores/${store.id}/edit`)}
                      >
                        <Edit className="w-3.5 h-3.5 mr-1.5" />
                        Editar
                      </Button>
                    )}
                    {canCreateTournament && (
                      <Button size="sm" onClick={() => navigate("/tournaments/create")}>
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        Crear Torneo
                      </Button>
                    )}
                    {store.urlTournaments && (
                      <Link
                        to={`/store/tournaments/${store.id}/${store.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Button size="sm" variant={canCreateTournament ? "secondary" : "default"}>
                          <Trophy className="w-3.5 h-3.5 mr-1.5" />
                          Ver Torneos
                        </Button>
                      </Link>
                    )}
                    {store.siteUrl && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(store.siteUrl, "_blank")}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        Sitio Web
                      </Button>
                    )}
                  </div>
                </div>

                {/* Level badge */}
                {store.Level && (
                  <div className="hidden sm:flex flex-col items-center gap-1.5 shrink-0">
                    {store.Level.icon && (
                      <img
                        src={store.Level.icon}
                        alt={store.Level.name}
                        className="w-10 h-10 object-contain"
                      />
                    )}
                    <Badge variant="secondary" className="text-xs">
                      {store.Level.name}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Phone className="w-4 h-4 text-primary" />
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {store.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-3.5 h-3.5 text-primary shrink-0" />
                    <span className="text-foreground">{store.phone}</span>
                  </div>
                )}
                {store.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-3.5 h-3.5 text-primary shrink-0" />
                    <span className="text-foreground">{store.email}</span>
                  </div>
                )}
                {store.siteUrl && (
                  <div className="flex items-center gap-3 text-sm">
                    <Globe className="w-3.5 h-3.5 text-primary shrink-0" />
                    <a
                      href={store.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline truncate"
                    >
                      {store.siteUrl}
                    </a>
                  </div>
                )}

                {(store.facebookUrl ||
                  store.instagramUrl ||
                  store.youtubeUrl ||
                  store.twitterUrl) && (
                  <>
                    <Separator />
                    <div className="flex flex-wrap gap-2">
                      {store.facebookUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(store.facebookUrl, "_blank")}
                        >
                          <Facebook className="w-3.5 h-3.5 mr-1.5" />
                          Facebook
                        </Button>
                      )}
                      {store.instagramUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(store.instagramUrl, "_blank")}
                        >
                          <Instagram className="w-3.5 h-3.5 mr-1.5" />
                          Instagram
                        </Button>
                      )}
                      {store.youtubeUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(store.youtubeUrl, "_blank")}
                        >
                          <Youtube className="w-3.5 h-3.5 mr-1.5" />
                          YouTube
                        </Button>
                      )}
                      {store.twitterUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(store.twitterUrl, "_blank")}
                        >
                          <Twitter className="w-3.5 h-3.5 mr-1.5" />
                          Twitter
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Address Information */}
            {store.Address && (
              <Card className="border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MapPin className="w-4 h-4 text-primary" />
                    Ubicación
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="font-semibold text-sm text-foreground">{store.Address.name}</p>
                    <p className="text-sm text-muted-foreground">{store.Address.address}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {store.Address.Location && (
                      <Badge variant="secondary" className="text-xs">
                        {store.Address.Location.name}
                      </Badge>
                    )}
                    {store.Address.regionId && (
                      <Badge variant="secondary" className="text-xs">
                        {regionsMap.get(store.Address.regionId).fullName}
                      </Badge>
                    )}
                    {store.Address.countryId && (
                      <Badge variant="secondary" className="text-xs gap-1.5">
                        <CountryFlag countryCode={countriesMap.get(store.Address?.countryId).iso} />
                        {countriesMap.get(store.Address.countryId).name}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tournament Days */}
            {store.StoreTournamentDays && store.StoreTournamentDays.length > 0 && (
              <Card className="border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Calendar className="w-4 h-4 text-primary" />
                    Días de Torneos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {[...store.StoreTournamentDays]
                      .sort((a, b) => a.TournamentDay.id - b.TournamentDay.id)
                      .map((day, index) => (
                        <Badge key={index} variant="secondary" className="text-xs px-2.5 py-1">
                          {day.TournamentDay.name}
                        </Badge>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Games */}
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Juegos Disponibles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {store.StoreGames.length ? (
                    store.StoreGames.map((storeGame, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className={`text-xs ${getGameBadgeClass(storeGame.Game.name)}`}
                      >
                        {storeGame.Game.name}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Sin juegos asociados</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Person in Charge */}
            {store.Person && (
              <Card className="border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Persona a Cargo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <img
                      src={store.Person.urlAvatar || randomPlaceholderAvatar(store.personId)}
                      alt={store.Person.fullName}
                      className="w-11 h-11 rounded-full object-cover ring-2 ring-border"
                      onError={(e) =>
                        (e.currentTarget.src = randomPlaceholderAvatar(store.Person.id))
                      }
                    />
                    <div>
                      <p className="font-semibold text-sm text-foreground">
                        {store.Person.fullName}
                      </p>
                      {store.Person.code && (
                        <p className="text-xs text-muted-foreground">{store.Person.code}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Additional Addresses */}
            {store.StoreAddresses && store.StoreAddresses.length > 0 && (
              <Card className="border-border/60">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MapPin className="w-4 h-4 text-primary" />
                    Direcciones Adicionales
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {store.StoreAddresses.map((storeAddress, index) => (
                    <div key={index} className="rounded-lg border border-border/60 bg-muted/30 p-3">
                      <p className="font-semibold text-sm text-foreground">
                        {storeAddress.Address.name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {storeAddress.Address.address}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {storeAddress.Address.Location && (
                          <Badge variant="secondary" className="text-[10px]">
                            {storeAddress.Address.Location.name}
                          </Badge>
                        )}
                        {storeAddress.Address.Region && (
                          <Badge variant="secondary" className="text-[10px]">
                            {storeAddress.Address.Region.fullName}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default StoreDetail
